import { MAX_RETURN_PERIOD_DAYS, isValidDayCount } from './return-conditions'

export type DeadlineKind = 'until' | 'days'

// Both values are kept so switching the kind back and forth loses nothing.
export interface Deadline {
  kind: DeadlineKind
  // Last day to start a return, YYYY-MM-DD.
  date: string
  days: number | null
}

// Orders purchased between start and end, both included and YYYY-MM-DD in UTC, get their own deadline.
export interface ReturnPeriodRule {
  start: string
  end: string
  deadline: Deadline
}

export const newReturnPeriodRule = (): ReturnPeriodRule => ({
  start: '',
  end: '',
  deadline: { kind: 'until', date: '', days: null }
})

const deadlineValue = ({ kind, date, days }: Deadline) =>
  kind === 'until' ? date : days

export const sameReturnPeriodRule = (
  rule: ReturnPeriodRule,
  stored: ReturnPeriodRule | undefined
) =>
  !!stored &&
  rule.start === stored.start &&
  rule.end === stored.end &&
  rule.deadline.kind === stored.deadline.kind &&
  deadlineValue(rule.deadline) === deadlineValue(stored.deadline)

// ISO dates compare as strings.
export const rangeValid = ({ start, end }: ReturnPeriodRule) =>
  !!start && !!end && start <= end

// Mirrors settings-api: a last day on or after the last purchase date, or a whole number of days.
export const deadlineValid = ({ end, deadline }: ReturnPeriodRule) =>
  deadline.kind === 'until'
    ? !!deadline.date && (!end || deadline.date >= end)
    : isValidDayCount(deadline.days, MAX_RETURN_PERIOD_DAYS)

export const returnPeriodRuleComplete = (rule: ReturnPeriodRule) =>
  rangeValid(rule) && deadlineValid(rule)

// Index of the first earlier rule whose range shares a day with this one, or -1.
export function overlappedBy(rules: ReturnPeriodRule[], index: number): number {
  const rule = rules[index]
  if (!rangeValid(rule)) return -1
  return rules
    .slice(0, index)
    .findIndex(
      (other) =>
        rangeValid(other) && other.start <= rule.end && rule.start <= other.end
    )
}

// The first rule holding the purchase date applies, as in returns-api.
export const matchingReturnPeriodRule = (
  rules: ReturnPeriodRule[],
  date: string
) =>
  rules.findIndex(
    (rule) => rangeValid(rule) && rule.start <= date && date <= rule.end
  )
